import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import {
  type BillApplicationFormValue,
  BillApplicationRow,
} from '../../../components/bookkeeping/BillApplicationRow.js';
import {
  type PayBillWithholdingFormValue,
  PayBillWithholdingRow,
} from '../../../components/bookkeeping/PayBillWithholdingRow.js';
import { SriStatusTrack } from '../../../components/bookkeeping/SriStatusTrack.js';
import type {
  Account,
  AccountNode,
  Bill,
  PayBill as PayBillType,
  Vendor,
  WithholdingRate,
} from '../../../types/bookkeeping.js';
import type { ConfigStatus } from '../../../types/onboarding.js';

const FX_SCALE = 1_000_000_000n;

function flattenTree(nodes: AccountNode[]): Account[] {
  const result: Account[] = [];
  function walk(ns: AccountNode[]) {
    for (const n of ns) {
      result.push(n);
      if (n.children.length > 0) walk(n.children);
    }
  }
  walk(nodes);
  return result;
}

function defaultApplication(): BillApplicationFormValue {
  return { bill_id: '', amount_applied: '' };
}

function defaultWithholding(): PayBillWithholdingFormValue {
  return { bill_id: '', withholding_rate_id: '', amount: '' };
}

interface PayBillDetail {
  pay_bill: PayBillType;
  applications: Array<{ bill_id: string; amount_applied: string }>;
  withholdings: Array<{ bill_id: string; withholding_rate_id: string; amount: string }>;
  retencion_id: string | null;
}

interface RetencionRow {
  id: string;
  number: string;
  clave_acceso: string;
  entry_date: string;
  period: string;
  total_retenido: string;
  is_signed: boolean;
  signed_at: string | null;
}

export function PayBillForm() {
  const { t } = useTranslation('bookkeeping');
  const nav = useNavigate();
  const qc = useQueryClient();
  const { activeOrgId } = useAuth();
  const { id: payBillId } = useParams<{ id: string }>();
  const isNew = !payBillId;

  const today = new Date().toISOString().slice(0, 10);
  const [vendorId, setVendorId] = useState('');
  const [paymentDate, setPaymentDate] = useState(today);
  const [paymentAccountId, setPaymentAccountId] = useState('');
  const [currency, setCurrency] = useState('');
  const [fxRate, setFxRate] = useState(FX_SCALE.toString());
  const [memo, setMemo] = useState('');
  const [applications, setApplications] = useState<BillApplicationFormValue[]>([
    defaultApplication(),
  ]);
  const [withholdings, setWithholdings] = useState<PayBillWithholdingFormValue[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Fetch config for base currency
  const { data: configData } = useQuery({
    queryKey: ['config-status', activeOrgId],
    queryFn: () => api<ConfigStatus>(`/orgs/${activeOrgId}/config-status`),
    enabled: !!activeOrgId,
  });
  const baseCurrency = configData?.organization.baseCurrency ?? 'USD';

  const effectiveCurrency = currency || baseCurrency;
  const sameCurrency = effectiveCurrency === baseCurrency || effectiveCurrency.length !== 3;

  // Auto-fetch fx rate
  const { data: fxData } = useQuery({
    queryKey: ['fx', effectiveCurrency, baseCurrency, paymentDate],
    queryFn: () =>
      api<{ rate: string; source: string }>(
        `/bookkeeping/fx-rates?date=${paymentDate}&from=${effectiveCurrency}&to=${baseCurrency}`,
      ),
    enabled: !!effectiveCurrency && effectiveCurrency.length === 3 && !sameCurrency,
    retry: false,
  });

  // Fetch vendors
  const { data: vendorsData } = useQuery({
    queryKey: ['bookkeeping-vendors', activeOrgId],
    queryFn: () => api<{ vendors: Vendor[] }>('/bookkeeping/vendors?limit=200'),
    enabled: !!activeOrgId,
  });
  const vendors = (vendorsData?.vendors ?? []).filter((v) => v.is_active);

  // Fetch open bills for the selected vendor
  const { data: openBillsData } = useQuery({
    queryKey: ['bk-open-bills', activeOrgId, vendorId],
    queryFn: () =>
      api<{ bills: Bill[] }>(
        `/bookkeeping/purchases/bills?vendor_id=${vendorId}&status=open&limit=100`,
      ),
    enabled: !!activeOrgId && !!vendorId,
  });
  // Also fetch partial bills and merge
  const { data: partialBillsData } = useQuery({
    queryKey: ['bk-partial-bills', activeOrgId, vendorId],
    queryFn: () =>
      api<{ bills: Bill[] }>(
        `/bookkeeping/purchases/bills?vendor_id=${vendorId}&status=partial&limit=100`,
      ),
    enabled: !!activeOrgId && !!vendorId,
  });

  const openBills = [...(openBillsData?.bills ?? []), ...(partialBillsData?.bills ?? [])];

  // Fetch accounts
  const { data: accountsData } = useQuery({
    queryKey: ['bookkeeping-accounts', activeOrgId],
    queryFn: () => api<{ tree: AccountNode[] }>('/bookkeeping/accounts'),
    enabled: !!activeOrgId,
  });
  const allAccounts = flattenTree(accountsData?.tree ?? []);
  const assetAccounts = allAccounts.filter((a) => a.type === 'asset' && a.is_active);

  // Fetch withholding rates
  const { data: withholdingRatesData } = useQuery({
    queryKey: ['bookkeeping-withholding-rates', activeOrgId],
    queryFn: () =>
      api<{ withholding_rates: WithholdingRate[] }>(
        '/bookkeeping/sales/withholding-rates?limit=100',
      ),
    enabled: !!activeOrgId,
  });
  const withholdingRates = (withholdingRatesData?.withholding_rates ?? []).filter(
    (r) => r.is_active,
  );

  // Load existing pay-bill if editing
  const payBillDetailQ = useQuery({
    queryKey: ['bk-pay-bill', payBillId],
    queryFn: () => api<PayBillDetail>(`/bookkeeping/purchases/pay-bills/${payBillId}`),
    enabled: !isNew && !!payBillId,
    onSuccess: (data: PayBillDetail) => {
      if (!seeded) {
        setVendorId(data.pay_bill.vendor_id);
        setPaymentDate(data.pay_bill.payment_date);
        setPaymentAccountId(data.pay_bill.payment_account_id);
        setCurrency(data.pay_bill.currency);
        setFxRate(data.pay_bill.fx_rate);
        setMemo(data.pay_bill.memo ?? '');
        setApplications(
          data.applications.length > 0
            ? data.applications.map((a) => ({
                bill_id: a.bill_id,
                amount_applied: a.amount_applied,
              }))
            : [defaultApplication()],
        );
        setWithholdings(
          data.withholdings.map((w) => ({
            bill_id: w.bill_id,
            withholding_rate_id: w.withholding_rate_id,
            amount: w.amount,
          })),
        );
        setSeeded(true);
      }
    },
  } as Parameters<typeof useQuery>[0]);

  // Cast back: the `as Parameters<typeof useQuery>[0]` above erases the generic.
  const retencionId = (payBillDetailQ.data as PayBillDetail | undefined)?.retencion_id ?? null;

  const retencionQ = useQuery({
    queryKey: ['retencion', retencionId],
    queryFn: () => api<RetencionRow>(`/bookkeeping/sri/withholding-receipts/${retencionId}`),
    enabled: !!retencionId,
  });

  // Live totals
  let grossBig = 0n;
  for (const app of applications) {
    try {
      if (app.amount_applied) grossBig += BigInt(app.amount_applied);
    } catch {
      // ignore
    }
  }

  let withholdingTotalBig = 0n;
  for (const wh of withholdings) {
    try {
      if (wh.amount) withholdingTotalBig += BigInt(wh.amount);
    } catch {
      // ignore
    }
  }

  const netBig = grossBig - withholdingTotalBig;

  // Bill options for withholdings — only bills that are in applications
  const billOptionsForWithholdings = applications
    .filter((a) => a.bill_id)
    .map((a) => {
      const bill = openBills.find((b) => b.id === a.bill_id);
      return bill ? { id: bill.id, number: bill.number } : null;
    })
    .filter(Boolean) as Array<{ id: string; number: string }>;

  const canSubmit =
    !!vendorId &&
    !!paymentDate &&
    !!paymentAccountId &&
    effectiveCurrency.length === 3 &&
    applications.length >= 1 &&
    applications.every((a) => a.bill_id && a.amount_applied);

  const submit = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({
        vendor_id: vendorId,
        payment_date: paymentDate,
        payment_account_id: paymentAccountId,
        currency: effectiveCurrency,
        fx_rate: sameCurrency ? FX_SCALE.toString() : (fxData?.rate ?? fxRate),
        memo: memo || null,
        applications: applications.map((a) => ({
          bill_id: a.bill_id,
          amount_applied: a.amount_applied,
        })),
        withholdings: withholdings.map((w) => ({
          bill_id: w.bill_id,
          withholding_rate_id: w.withholding_rate_id,
          amount: w.amount,
        })),
      });
      return isNew
        ? api('/bookkeeping/purchases/pay-bills', { method: 'POST', body })
        : api(`/bookkeeping/purchases/pay-bills/${payBillId}`, { method: 'PATCH', body });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['bk-pay-bills'] });
      if (!isNew) await qc.invalidateQueries({ queryKey: ['bk-pay-bill', payBillId] });
      nav('/app/bookkeeping/purchases/pay-bills');
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setSubmitError(err.body?.error ?? err.message ?? 'Error desconocido');
    },
  });

  return (
    <div className="space-y-4">
      {/* Title bar */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          {isNew
            ? t('purchases.pay_bills.new')
            : t('purchases.pay_bills.editTitle', { defaultValue: 'Editar pago a proveedor' })}
        </h1>
        <button
          type="button"
          onClick={() => nav('/app/bookkeeping/purchases/pay-bills')}
          className="rounded border px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          {t('journalEntries.cancel')}
        </button>
      </div>

      {/* Header fields */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {/* Vendor (required) */}
        <div className="col-span-2 md:col-span-1">
          <label className="block text-xs text-slate-500">
            {t('purchases.pay_bills.fields.vendor')} *
            <select
              value={vendorId}
              onChange={(e) => {
                setVendorId(e.target.value);
                // Reset applications and withholdings when vendor changes
                setApplications([defaultApplication()]);
                setWithholdings([]);
              }}
              className="mt-0.5 w-full rounded border px-2 py-1 text-sm"
              required
            >
              <option value="">— {t('purchases.pay_bills.fields.vendor')}</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.display_name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Payment date */}
        <div>
          <label className="block text-xs text-slate-500">
            {t('purchases.pay_bills.fields.payment_date')}
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
            />
          </label>
        </div>

        {/* Payment account (required) */}
        <div>
          <label className="block text-xs text-slate-500">
            {t('purchases.pay_bills.fields.payment_account', { defaultValue: 'Cuenta de pago' })} *
            <select
              value={paymentAccountId}
              onChange={(e) => setPaymentAccountId(e.target.value)}
              className="mt-0.5 w-full rounded border px-2 py-1 text-sm"
              required
            >
              <option value="">
                —{' '}
                {t('purchases.pay_bills.fields.payment_account', {
                  defaultValue: 'Cuenta de pago',
                })}
              </option>
              {assetAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} {a.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Currency */}
        <div>
          <label className="block text-xs text-slate-500">
            {t('purchases.pay_bills.fields.currency')}
            <input
              value={effectiveCurrency}
              maxLength={3}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              className="mt-0.5 block w-full rounded border px-2 py-1 text-sm uppercase"
            />
          </label>
        </div>

        {/* FX rate */}
        <div>
          <label className="block text-xs text-slate-500">
            {t('journalEntries.columns.fxRate')}
            <input
              value={sameCurrency ? FX_SCALE.toString() : (fxData?.rate ?? fxRate)}
              disabled={sameCurrency}
              onChange={(e) => setFxRate(e.target.value)}
              className="mt-0.5 block w-full rounded border px-2 py-1 text-sm disabled:bg-slate-50 disabled:text-slate-400"
            />
          </label>
        </div>

        {/* Memo */}
        <div className="col-span-2 md:col-span-3">
          <label className="block text-xs text-slate-500">
            {t('journalEntries.fields.memo')}
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              rows={2}
              placeholder={t('journalEntries.fields.memoPlaceholder')}
              className="mt-0.5 block w-full rounded border px-2 py-1 text-sm"
            />
          </label>
        </div>
      </div>

      {/* Applications section */}
      <div>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">
          {t('purchases.pay_bills.applications', { defaultValue: 'Facturas aplicadas' })}
        </h2>
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-2 py-2">{t('purchases.bills.title')}</th>
                <th className="px-2 py-2 text-right">
                  {t('purchases.bills.fields.grand_total', { defaultValue: 'Saldo abierto' })}
                </th>
                <th className="px-2 py-2">
                  {t('purchases.pay_bills.amount_applied', { defaultValue: 'Monto aplicado' })}
                </th>
                <th className="px-2 py-2 text-right">
                  {t('purchases.pay_bills.remaining', { defaultValue: 'Restante' })}
                </th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {applications.map((app, i) => (
                <BillApplicationRow
                  // biome-ignore lint/suspicious/noArrayIndexKey: bill application rows are ordered form rows with no stable ID
                  key={i}
                  index={i}
                  value={app}
                  openBills={openBills.map((b) => ({
                    id: b.id,
                    number: b.number,
                    entry_date: b.entry_date,
                    grand_total: b.grand_total,
                    paid_amount: b.paid_amount,
                  }))}
                  canRemove={applications.length > 1}
                  onChange={(v) => {
                    setSubmitError(null);
                    setApplications((as) => as.map((x, j) => (i === j ? v : x)));
                  }}
                  onRemove={() => setApplications((as) => as.filter((_, j) => j !== i))}
                />
              ))}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          onClick={() => setApplications((as) => [...as, defaultApplication()])}
          className="mt-2 rounded border px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          + {t('journalEntries.addLine')}
        </button>
      </div>

      {/* Withholdings section */}
      <div>
        <h2 className="mb-2 text-sm font-semibold text-slate-700">
          {t('sales.receive_payments.withholdings')}
        </h2>
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-2 py-2">{t('purchases.bills.title')}</th>
                <th className="px-2 py-2">
                  {t('sales.withholding_rates.title', { defaultValue: 'Tasa de retención' })}
                </th>
                <th className="px-2 py-2">{t('sales.line.total')}</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {withholdings.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-3 text-center text-xs text-slate-400">
                    {t('sales.receive_payments.noWithholdings', {
                      defaultValue: 'Sin retenciones',
                    })}
                  </td>
                </tr>
              ) : (
                withholdings.map((wh, i) => (
                  <PayBillWithholdingRow
                    // biome-ignore lint/suspicious/noArrayIndexKey: withholding rows are ordered form rows with no stable ID
                    key={i}
                    index={i}
                    value={wh}
                    billOptions={billOptionsForWithholdings}
                    withholdingRates={withholdingRates}
                    canRemove={true}
                    onChange={(v) => {
                      setSubmitError(null);
                      setWithholdings((ws) => ws.map((x, j) => (i === j ? v : x)));
                    }}
                    onRemove={() => setWithholdings((ws) => ws.filter((_, j) => j !== i))}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          onClick={() => setWithholdings((ws) => [...ws, defaultWithholding()])}
          className="mt-2 rounded border px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          + {t('journalEntries.addLine')}
        </button>
      </div>

      {/* Footer totals + submit */}
      <div className="rounded border bg-slate-50 px-4 py-3">
        <div className="flex flex-col items-end gap-1 text-sm">
          <div className="flex gap-4">
            <span className="text-slate-500">{t('purchases.pay_bills.fields.gross')}:</span>
            <span className="font-mono">
              {grossBig.toString()} {effectiveCurrency}
            </span>
          </div>
          <div className="flex gap-4">
            <span className="text-slate-500">
              {t('purchases.pay_bills.fields.withholding_total')}:
            </span>
            <span className="font-mono">
              {withholdingTotalBig.toString()} {effectiveCurrency}
            </span>
          </div>
          <div className="flex gap-4 font-medium">
            <span className="text-slate-700">{t('purchases.pay_bills.fields.net')}:</span>
            <span className={`font-mono ${netBig < 0n ? 'text-red-600' : ''}`}>
              {netBig.toString()} {effectiveCurrency}
            </span>
          </div>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => nav('/app/bookkeeping/purchases/pay-bills')}
            className="rounded border px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
          >
            {t('journalEntries.cancel')}
          </button>
          <button
            type="button"
            disabled={!canSubmit || submit.isPending || netBig < 0n}
            onClick={() => {
              setSubmitError(null);
              submit.mutate();
            }}
            className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50"
          >
            {submit.isPending ? '…' : t('journalEntries.submit')}
          </button>
        </div>
      </div>

      {submitError && <p className="text-sm text-red-600">{submitError}</p>}

      {/* SRI delivery block — only for saved pay-bills with a withholding receipt */}
      {retencionQ.data && (
        <SriStatusTrack docType="withholding-receipts" docId={retencionQ.data.id} />
      )}
    </div>
  );
}
